// src/modules/app-views/products/pages/Document/tabs/StockTab/AddressCard.jsx
// *******************************************************************************
// STOCK ADDRESS CARD
// *******************************************************************************
// Manages product storage locations (addresses) within warehouses.
// A product can have multiple locations (e.g., Shelf 01, Shelf 03).
// Uses Cascader to navigate the warehouse hierarchy tree.
// Saves as Form field 'stockLocations' (array of location paths).
// Display format: TAG [A01-R1-P2] + full path A01-Principal | R1-Rua 01 | P2-Prateleira 2
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useCallback } from 'react';
import { Card, Button, Flex, Typography, Tag, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import SimpleCascader from 'components/SimpleComponents/SimpleCascader';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AddressCard = ({ style: styleProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const { form } = useSimpleFormContext() || {};
  const { viewMode } = useDocumentContext();
  const isLocked = viewMode === 'view';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [pendingLocation, setPendingLocation] = useState(null);
  // Counter to force re-render of the locations list after add/remove
  const [listVersion, setListVersion] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Build cascader options and raw warehouse data from hierarchy
  const { warehouseOptions, rawWarehouses } = useMemo(() => {
    const raw = appService.getModulePreferences({
      moduleId: 'products',
      category: 'warehouses',
    });

    const safeRaw = raw && Array.isArray(raw) ? raw : [];
    const coloredRaw = assignMissingColors(safeRaw);

    return {
      warehouseOptions: transformToCascader(coloredRaw),
      rawWarehouses: coloredRaw,
    };
  }, [storedParameters]);

  // Read locations from form (snapshot, not reactive — listVersion triggers re-render)
  const currentLocations = form?.getFieldValue('stockLocations') || [];

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCascaderChange = useCallback(value => {
    setPendingLocation(value && value.length > 0 ? value : null);
  }, []);

  const handleAddLocation = useCallback(() => {
    if (!pendingLocation || pendingLocation.length === 0) return;

    const locations = form?.getFieldValue('stockLocations') || [];
    const locationKey = pendingLocation.join('/');

    // Check if already added
    const existing = locations.find(loc => loc.path?.join('/') === locationKey);
    if (existing) {
      appService.message({ flag: 'w', text: 'item_already_exists' });
      return;
    }

    // Resolve labels, codes, and root color for display
    const resolved = resolveLocationData(rawWarehouses, warehouseOptions, pendingLocation);

    const newLocation = {
      path: pendingLocation,
      labels: resolved.labels,
      codes: resolved.codes,
      rootColor: resolved.rootColor,
      key: locationKey,
    };

    const updated = [...locations, newLocation];
    form?.setFieldsValue({ stockLocations: updated });

    // Reset cascader
    form?.setFieldValue('_pendingLocationCascader', undefined);
    setPendingLocation(null);
    setListVersion(v => v + 1);
  }, [pendingLocation, form, warehouseOptions, rawWarehouses]);

  const handleRemoveLocation = useCallback(
    key => {
      const locations = form?.getFieldValue('stockLocations') || [];
      const updated = locations.filter(loc => loc.key !== key);
      form?.setFieldsValue({ stockLocations: updated });
      setListVersion(v => v + 1);
    },
    [form],
  );

  // Custom display for cascader: CODE-Name | CODE-Name
  const displayRender = useCallback(labels => {
    return labels.join(' | ');
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card
      title={<div className="text-muted">{translateX('stock_address')}</div>}
      style={{ minHeight: 248, ...styleProp }}
    >
      {/* Add location row */}
      <Flex gap={8} align="end" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <SimpleCascader
            name="_pendingLocationCascader"
            label="location"
            options={warehouseOptions}
            placeholder="select_location"
            onChange={handleCascaderChange}
            displayRender={displayRender}
            changeOnSelect
          />
        </div>
        <Button
          icon={<PlusOutlined />}
          onClick={handleAddLocation}
          disabled={isLocked || !pendingLocation || pendingLocation.length === 0}
          style={{ height: 40, marginBottom: 24 }}
        >
          {translateX('add')}
        </Button>
      </Flex>

      {/* Locations list — listVersion forces re-read from form */}
      <Flex vertical gap={8} key={listVersion}>
        {currentLocations.map(loc => {
          // Resolve labels/codes from current params (not from saved snapshot)
          const resolved = resolveLocationData(rawWarehouses, warehouseOptions, loc.path || []);
          const codeTag = (resolved.codes || loc.codes || []).join('-');
          const fullPath = (resolved.labels || loc.labels || []).join(' | ');
          const tagColor = resolved.rootColor || loc.rootColor || '#1677ff';

          return (
            <Flex
              key={loc.key}
              align="center"
              justify="space-between"
              style={{
                padding: '8px 12px',
                background: 'var(--ant-color-fill-alter)',
                borderRadius: 6,
                border: '1px solid var(--ant-color-border-secondary)',
              }}
            >
              <Flex align="center" gap={8}>
                <EnvironmentOutlined style={{ color: '#8c8c8c' }} />
                <Tag color={tagColor} style={{ margin: 0, fontWeight: 600 }}>
                  {codeTag}
                </Tag>
                <Text>{fullPath}</Text>
              </Flex>
              <Popconfirm
                title={translateX('confirm_delete')}
                onConfirm={() => handleRemoveLocation(loc.key)}
                okText={translateX('yes')}
                cancelText={translateX('no')}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={isLocked} />
              </Popconfirm>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AddressCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build cascader options from warehouse hierarchy tree.
 */
function transformToCascader(nodes) {
  if (!Array.isArray(nodes)) return [];

  return nodes.map(node => {
    const label = node.code ? `${node.code}-${node.name}` : node.name;
    const children = node.children?.length > 0 ? transformToCascader(node.children) : undefined;

    return {
      value: node.uid,
      label,
      children,
    };
  });
}

/**
 * Resolve UIDs path to display labels, codes, and root warehouse color.
 */
function resolveLocationData(rawWarehouses, cascaderOptions, path) {
  const labels = [];
  const codes = [];
  let rootColor = null;

  if (path.length > 0) {
    const rootNode = findNodeByUid(rawWarehouses, path[0]);
    rootColor = rootNode?.color || '#1677ff';
  }

  let currentLevel = cascaderOptions;
  for (const uid of path) {
    const found = currentLevel?.find(opt => opt.value === uid);
    if (found) {
      labels.push(found.label);
      const dashIdx = found.label.indexOf('-');
      codes.push(dashIdx > 0 ? found.label.substring(0, dashIdx) : found.label);
      currentLevel = found.children || [];
    } else {
      labels.push(uid);
      codes.push(uid);
    }
  }

  return { labels, codes, rootColor };
}

/**
 * Find a node by uid in the raw warehouse hierarchy.
 */
function findNodeByUid(nodes, uid) {
  if (!Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (node.uid === uid) return node;
    if (node.children) {
      const found = findNodeByUid(node.children, uid);
      if (found) return found;
    }
  }
  return null;
}

const WAREHOUSE_COLORS = [
  '#1677ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
  '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d',
  '#597ef7', '#36cfc9', '#ff7a45', '#9254de', '#ffc53d',
];

function assignMissingColors(nodes) {
  const needsColor = nodes.some(n => !n.color);
  if (!needsColor) return nodes;

  const usedColors = nodes.map(n => n.color).filter(Boolean);
  const updated = nodes.map(node => {
    if (node.color) return node;

    const available = WAREHOUSE_COLORS.filter(c => !usedColors.includes(c));
    const color = available.length > 0
      ? available[Math.floor(Math.random() * available.length)]
      : WAREHOUSE_COLORS[Math.floor(Math.random() * WAREHOUSE_COLORS.length)];

    usedColors.push(color);
    return { ...node, color };
  });

  appService.updateModulePreferences({
    moduleId: 'products',
    category: 'warehouses',
    data: updated,
  });

  return updated;
}
